import { Callout, FileCard, Icon, Link } from '@dev-spendesk/grapes';
import { useState } from 'react';

import { DownloadEInvoiceXmlLink } from 'src/core/common/components/DownloadEInvoiceXmlLink/DownloadEInvoiceXmlLink';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { useUser } from 'src/core/modules/app/hooks/useUser';

import {
  HELP_CENTER_SPANSIH_DPR_LINK,
  SpanishDPRModal,
} from '../../SpanishDPR/SpanishDPRModal';

type Props = {
  fileName: string;
  isHighlighted?: boolean;
  mimeType?: string;
  onPreview?: () => void;
  onDelete?: () => void;
  facturXLink?: string;
  description?: string;
} & (
  | {
      requesterId: string;
      spainEreceiptState:
        | 'ongoing'
        | 'done'
        | 'failed'
        | 'not-applicable'
        | undefined;
    }
  | {
      requesterId?: undefined;
      spainEreceiptState?: undefined;
    }
);

export const PreviewFileItem = ({
  fileName,
  isHighlighted = false,
  mimeType,
  onPreview,
  onDelete,
  facturXLink,
  requesterId = '',
  spainEreceiptState,
  description,
}: Props) => {
  const { t } = useTranslation('global');
  const user = useUser();
  const hasSpanishDPR = useFeature(FEATURES.SPANISH_DPR);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const shouldDisplaySpanishDprError =
    hasSpanishDPR && spainEreceiptState === 'failed' && requesterId === user.id;

  return (
    <div className="mb-8 [&:last-of-type]:mb-0">
      <div className="rounded-12 bg-background-secondary-default">
        <FileCard
          isHighlighted={isHighlighted}
          onClick={onPreview}
          title={fileName}
          mimeType={mimeType}
          description={description}
          onDelete={onDelete}
          variant={shouldDisplaySpanishDprError ? 'alert' : 'neutral'}
        />
        {hasSpanishDPR && spainEreceiptState === 'ongoing' && (
          <div className="flex items-center gap-4 px-16 py-8 text-content-secondary-bg-secondary body-s">
            <Icon size="s" name="clock-filled" />
            {t('requests.panel.spanishDPR.previewOngoing')}
            <span className="text-content-disabled">|</span>
            <Link
              href={HELP_CENTER_SPANSIH_DPR_LINK}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('requests.panel.spanishDPR.calloutReadMore')}
            </Link>
          </div>
        )}
        {hasSpanishDPR && spainEreceiptState === 'done' && (
          <div className="flex items-center gap-4 px-16 py-8 text-content-secondary-bg-secondary body-s">
            <Icon size="s" name="shield-star" />
            {t('requests.panel.spanishDPR.previewDone')}
            <span className="text-content-disabled">|</span>
            <Link
              href={HELP_CENTER_SPANSIH_DPR_LINK}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('requests.panel.spanishDPR.calloutReadMore')}
            </Link>
          </div>
        )}
        {facturXLink && (
          <DownloadEInvoiceXmlLink
            downloadXmlUrl={facturXLink}
            className="px-16 py-8"
          />
        )}
      </div>
      {shouldDisplaySpanishDprError && (
        <Callout
          className="mt-8"
          variant="alert"
          title={t('requests.panel.spanishDPR.previewFailed')}
        >
          <div>
            {t('requests.panel.spanishDPR.previewFailedContent')}
            <div className="flex gap-4">
              <Link
                href={HELP_CENTER_SPANSIH_DPR_LINK}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('requests.panel.spanishDPR.calloutReadMore')}
              </Link>
              {' • '}
              <Link
                onClick={(event) => {
                  event.preventDefault();
                  setIsModalOpen(true);
                }}
                href=""
              >
                {t('requests.panel.spanishDPR.calloutSwitchToMobile')}
              </Link>
            </div>
          </div>
        </Callout>
      )}
      <SpanishDPRModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};
